import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/BlockchainExplorer.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api';

const BlockchainExplorer = () => {
  const navigate = useNavigate();
  const [blocks, setBlocks]             = useState([]);
  const [currentPage, setCurrentPage]   = useState(0);
  const [totalPages, setTotalPages]     = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [searchQuery, setSearchQuery]   = useState('');
  const [searchType, setSearchType]     = useState('dataType'); // 'dataType' | 'refId'
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState('');
  const pollingRef                      = useRef(null);
  const PAGE_SIZE                       = 20;

  const token = () => {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    return u.token || '';
  };

  const headers = () => ({
    'Authorization': `Bearer ${token()}`,
    'Content-Type': 'application/json',
  });

  const fetchBlocks = useCallback(async (page = 0, query = '', type = 'dataType') => {
    setLoading(true); setError('');
    try {
      let url;
      if (query.trim()) {
        const param = type === 'refId' ? `refId=${encodeURIComponent(query)}` : `dataType=${encodeURIComponent(query)}`;
        url = `${API_BASE}/blockchain/blocks/search?${param}&page=${page}&size=${PAGE_SIZE}`;
      } else {
        url = `${API_BASE}/blockchain/blocks?page=${page}&size=${PAGE_SIZE}`;
      }
      const res = await fetch(url, { headers: headers() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setBlocks(data.content || []);
      setTotalPages(data.totalPages || 0);
      setTotalElements(data.totalElements || 0);
    } catch (e) {
      setError('Không thể tải danh sách block: ' + e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchBlocks(currentPage, searchQuery, searchType);
  }, [currentPage]);

  // Polling every 5s for new blocks (only on page 0 when no search)
  useEffect(() => {
    pollingRef.current = setInterval(() => {
      if (currentPage === 0 && !searchQuery.trim()) {
        fetchBlocks(0, '', searchType);
      }
    }, 5000);
    return () => clearInterval(pollingRef.current);
  }, [currentPage, searchQuery, searchType, fetchBlocks]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(0);
    fetchBlocks(0, searchQuery, searchType);
  };

  const formatHash = (hash, len = 16) =>
    hash ? hash.substring(0, len) + '…' : 'N/A';

  const formatTimestamp = (ts) => {
    if (!ts) return 'N/A';
    return new Date(ts).toLocaleString('vi-VN');
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'MATCH':       return '#4caf50';
      case 'TRANSACTION': return '#2196f3';
      case 'AUDIT':       return '#ff9800';
      default:            return '#9e9e9e';
    }
  };

  return (
    <div className="bc-explorer">
      <div className="bc-header">
        <div className="bc-header-left">
          <h1>🔗 Blockchain Explorer</h1>
          <p className="bc-subtitle">{totalElements} blocks trên chain</p>
        </div>
        <div className="bc-header-actions">
          <button className="bc-action-btn" onClick={() => navigate('/home')}>🏠 Home</button>
          <button className="bc-action-btn" onClick={() => navigate('/security/dashboard')}>🛡️ Security</button>
        </div>
      </div>

      {/* Search */}
      <form className="bc-search-bar" onSubmit={handleSearch}>
        <select
          value={searchType}
          onChange={e => setSearchType(e.target.value)}
          className="bc-search-type"
        >
          <option value="dataType">Data Type</option>
          <option value="refId">Reference ID</option>
        </select>
        <input
          type="text"
          placeholder={searchType === 'dataType' ? 'MATCH / TRANSACTION / AUDIT' : 'Enter Reference ID…'}
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="bc-search-input"
        />
        <button type="submit" className="bc-search-btn">🔍 Tìm</button>
        {searchQuery && (
          <button type="button" className="bc-clear-btn" onClick={() => { setSearchQuery(''); setCurrentPage(0); fetchBlocks(0, '', searchType); }}>✕</button>
        )}
      </form>

      {error && <div className="bc-error">{error}</div>}

      {loading ? (
        <div className="bc-loading">⏳ Đang tải blockchain…</div>
      ) : (
        <>
          {/* Table */}
          <div className="bc-table-wrap">
            <table className="bc-table">
              <thead>
                <tr>
                  <th>Block #</th>
                  <th>Timestamp</th>
                  <th>Prev Hash</th>
                  <th>Current Hash</th>
                  <th>Type</th>
                  <th>Ref ID</th>
                </tr>
              </thead>
              <tbody>
                {blocks.length === 0 ? (
                  <tr><td colSpan={6} className="bc-empty">📭 Chưa có block nào</td></tr>
                ) : (
                  blocks.map((b, i) => (
                    <tr key={i} className="bc-row" onClick={() => setSelectedBlock(b)}>
                      <td className="bc-index">#{b.blockIndex ?? '—'}</td>
                      <td className="bc-ts">{formatTimestamp(b.timestamp)}</td>
                      <td className="bc-hash" title={b.prevHashShort}>{formatHash(b.prevHashShort, 16)}</td>
                      <td className="bc-hash" title={b.currentHashShort}>{formatHash(b.currentHashShort, 16)}</td>
                      <td>
                        <span className="bc-type-badge" style={{ background: getTypeColor(b.dataType) }}>
                          {b.dataType}
                        </span>
                      </td>
                      <td className="bc-refid">{b.referenceId}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bc-pagination">
              <button onClick={() => setCurrentPage(p => Math.max(0, p - 1))} disabled={currentPage === 0}>← Trước</button>
              <span>Trang {currentPage + 1} / {totalPages}</span>
              <button onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))} disabled={currentPage >= totalPages - 1}>Tiếp →</button>
            </div>
          )}
        </>
      )}

      {/* Detail Modal */}
      {selectedBlock && (
        <div className="bc-modal-overlay" onClick={() => setSelectedBlock(null)}>
          <div className="bc-modal" onClick={e => e.stopPropagation()}>
            <div className="bc-modal-header">
              <h2>Block #{selectedBlock.blockIndex}</h2>
              <button className="bc-modal-close" onClick={() => setSelectedBlock(null)}>✕</button>
            </div>
            <div className="bc-modal-body">
              <div className="bc-detail-row"><span>Timestamp:</span><span>{formatTimestamp(selectedBlock.timestamp)}</span></div>
              <div className="bc-detail-row"><span>Data Type:</span><span className="bc-type-badge" style={{ background: getTypeColor(selectedBlock.dataType) }}>{selectedBlock.dataType}</span></div>
              <div className="bc-detail-row"><span>Reference ID:</span><span>{selectedBlock.referenceId}</span></div>
              <div className="bc-detail-row bc-hash-row"><span>Previous Hash:</span><code className="bc-full-hash">{selectedBlock.previousHash || selectedBlock.prevHashShort}</code></div>
              <div className="bc-detail-row bc-hash-row"><span>Current Hash:</span><code className="bc-full-hash">{selectedBlock.currentHash || selectedBlock.currentHashShort}</code></div>
              {selectedBlock.dataHash && (
                <div className="bc-detail-row bc-hash-row"><span>Data Hash:</span><code className="bc-full-hash">{selectedBlock.dataHash}</code></div>
              )}
              {selectedBlock.encryptedBackupPreview && (
                <div className="bc-detail-row bc-hash-row">
                  <span>Encrypted Backup (preview):</span>
                  <code className="bc-backup-preview">{selectedBlock.encryptedBackupPreview}…</code>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlockchainExplorer;
